import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { StripePrice, Sponsor, StripePromotionCode, Currency } from '@/utils/types';
import { checkVatCode, getDetectedCountry, calculateVat, getStripePromotionCode } from '@/utils/api';
import { getPriceForCurrency } from '@/utils/prices';

export type CartState = {
  prices: StripePrice[];
  vatCode: string;
  vatRate: number;
  vatAmount: number;
  totalWithoutVat: number;
  totalIncludingVat: number;
  isVatNumberValid: 'unknown' | 'valid' | 'invalid';
  countryCode: string;
  postCode: string;
  couponCode: string;
  coupon?: StripePromotionCode['coupon'];
  couponAmount: number;
  currency: Currency;
  sponsor?: Sponsor;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
};

type CartContext = CartState & {
  setCartPartialState: (partialState: Partial<CartState>) => void;
};

type CartProviderProps = {
  children: ReactNode;
};

const CartContext = createContext<CartContext | undefined>(undefined);

export const CartProvider = ({ children }: CartProviderProps) => {
  const [cartState, setCartState] = useState<CartState>({
    prices: [],
    vatCode: '',
    vatRate: 0,
    vatAmount: 0,
    totalWithoutVat: 0,
    totalIncludingVat: 0,
    isVatNumberValid: 'unknown',
    countryCode: '',
    postCode: '',
    couponCode: '',
    couponAmount: 0,
    coupon: undefined,
    currency: 'eur',
    stripeCustomerId: undefined,
    stripeSubscriptionId: undefined,
  });

  const setCartPartialState = useCallback((partialState: Partial<CartState>) => {
    setCartState(prevState => ({
      ...prevState,
      ...partialState,
    }));
  }, []);

  const verifyVatCodeValidity = useCallback(
    async (vatCode: string) => {
      if (!vatCode || vatCode === '') return;
      const response = await checkVatCode(vatCode);
      setTimeout(() => {
        setCartPartialState({
          isVatNumberValid: 'valid_vat_id' in response ? (response.valid_vat_id ? 'valid' : 'invalid') : 'unknown',
        });
      }, 300);
    },
    [setCartPartialState],
  );

  const getCoupon = useCallback(
    async (couponCode: string) => {
      if (!couponCode || couponCode === '') {
        setCartPartialState({ coupon: undefined });
        return;
      }

      const promoCodeObject = await getStripePromotionCode(couponCode);
      const coupon = promoCodeObject.coupon;

      if (coupon) {
        setCartPartialState({ coupon });
      }
    },
    [setCartPartialState],
  );

  const getCouponDiscount = useCallback(
    (sumOfPrices: number) => {
      let couponAmount = 0;

      if (cartState?.coupon?.amount_off) {
        couponAmount = cartState.coupon.amount_off / 100;
      } else if (cartState?.coupon?.percent_off) {
        couponAmount = (cartState.coupon.percent_off / 100) * sumOfPrices;
      }
      return couponAmount;
    },
    [cartState.coupon],
  );

  const detectCountry = useCallback(async () => {
    const result = await getDetectedCountry();
    setCartPartialState({
      countryCode: result.country_code || 'FR',
    });
  }, [setCartPartialState]);

  const calculateVatInfos = useCallback(
    async (vatCode: string, countryCode: string, postCode: string) => {
      const result = await calculateVat(cartState.totalWithoutVat, countryCode, postCode, vatCode);
      setCartPartialState({
        vatRate: 'tax_rate' in result ? result.tax_rate : 0,
      });
    },
    [cartState.totalWithoutVat, setCartPartialState],
  );

  useEffect(() => {
    detectCountry();
  }, [detectCountry]);

  useEffect(() => {
    const sumWithoutVat = cartState.prices.reduce(
      (acc, price) => acc + getPriceForCurrency(price, cartState.currency),
      0,
    );

    const totalWithoutVat = sumWithoutVat - cartState.couponAmount;
    let vatAmount = cartState.vatRate * totalWithoutVat;

    if (cartState.isVatNumberValid === 'valid') {
      vatAmount = 0;
    }

    const totalIncludingVat = totalWithoutVat + vatAmount;

    const couponAmount = getCouponDiscount(sumWithoutVat);

    setCartPartialState({
      totalWithoutVat,
      totalIncludingVat,
      vatAmount,
      couponAmount,
    });
  }, [
    cartState.prices,
    cartState.currency,
    cartState.vatRate,
    cartState.vatAmount,
    cartState.coupon,
    cartState.isVatNumberValid,
    cartState.couponAmount,
    setCartPartialState,
    getCouponDiscount,
  ]);

  useEffect(() => {
    if (cartState.vatCode) {
      verifyVatCodeValidity(cartState.vatCode);
    }
  }, [cartState.vatCode, verifyVatCodeValidity]);

  useEffect(() => {
    getCoupon(cartState.couponCode);
  }, [cartState.couponCode, getCoupon]);

  useEffect(() => {
    if (cartState.countryCode) {
      calculateVatInfos(cartState.vatCode, cartState.countryCode, cartState.postCode);
    }
  }, [cartState.countryCode, cartState.postCode, cartState.vatCode, calculateVatInfos]);

  return (
    <CartContext.Provider
      value={{
        ...cartState,
        setCartPartialState,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = () => {
  const context = useContext(CartContext);

  if (context === undefined) {
    throw new Error('useCartContext must be used within a CartProvider');
  }

  return context;
};
